(() => {
  'use strict';

  const root = document.getElementById('RedfoxAutoLanguage');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  root.dataset.languageStatus = 'ready';

  const supported = new Set(root.dataset.availableLocales.split(','));
  const allowed = new Set(['en', 'zh-CN', 'zh-TW']);
  const preferenceKey = 'redfox:language:manual:v1';
  const sessionKey = 'redfox:language:session:v1';
  const redirectKey = 'redfox:language:redirect:v1';
  const preferenceLifetime = 180 * 24 * 60 * 60 * 1000;
  let engaged = false;
  let leaving = false;
  let controller;

  function read(storage, key) {
    try {
      return window[storage].getItem(key);
    } catch (_) {
      return null;
    }
  }

  function write(storage, key, value) {
    try {
      window[storage].setItem(key, value);
      return window[storage].getItem(key) === value;
    } catch (_) {
      return false;
    }
  }

  function manualPreference() {
    let newest = null;
    for (const storage of ['localStorage', 'sessionStorage']) {
      try {
        const saved = JSON.parse(read(storage, preferenceKey));
        if (saved && allowed.has(saved.locale) && supported.has(saved.locale) &&
            Number.isFinite(saved.expiresAt) && saved.expiresAt > Date.now() &&
            (!newest || saved.expiresAt >= newest.expiresAt)) newest = saved;
      } catch (_) {
        // Try the session fallback when persistent storage is unavailable.
      }
    }
    return newest ? newest.locale : null;
  }

  function returnPath() {
    return window.location.pathname.replace(/^\/+/, '/') + window.location.search + window.location.hash;
  }

  function keepReturnPath(form) {
    let input = form.querySelector('[name="return_to"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      form.appendChild(input);
    }
    // A leading double slash must never turn a storefront path into an external redirect.
    input.value = returnPath();
  }

  function cancelAutomatic() {
    engaged = true;
    root.dataset.languageStatus = 'visitor-interaction';
    if (controller) controller.abort();
  }

  // Capture before Dawn's direct form.submit(), which does not emit a submit event.
  document.addEventListener('click', (event) => {
    const choice = event.target.closest('localization-form a[data-value][hreflang]');
    if (!choice) return;
    const form = choice.closest('form');
    const locale = choice.dataset.value;
    if (!form || !form.querySelector('[name="locale_code"]') || !allowed.has(locale) || !supported.has(locale)) return;
    cancelAutomatic();
    const preference = JSON.stringify({ locale, expiresAt: Date.now() + preferenceLifetime });
    write('localStorage', preferenceKey, preference);
    write('sessionStorage', preferenceKey, preference);
    write('sessionStorage', redirectKey, '');
    // This also protects the current session when persistent storage is disabled.
    write('sessionStorage', sessionKey, 'manual:' + locale);
    keepReturnPath(form);
  }, true);

  ['pointerdown', 'keydown', 'input', 'change', 'submit'].forEach((eventName) => {
    document.addEventListener(eventName, cancelAutomatic, true);
  });
  window.addEventListener('pagehide', () => {
    leaving = true;
    if (controller) controller.abort();
  });

  function excluded() {
    const shopify = window.Shopify || {};
    const location = window.location;
    const params = new URLSearchParams(location.search);
    const routeRoot = root.dataset.rootUrl.replace(/\/$/, '');
    const path = routeRoot && location.pathname.startsWith(routeRoot + '/')
      ? location.pathname.slice(routeRoot.length) : location.pathname;
    return shopify.designMode || shopify.visualPreviewMode || window.self !== window.top ||
      location.hostname.endsWith('.shopifypreview.com') ||
      /bot|crawler|spider|crawling|slurp|facebookexternalhit|bingpreview/i.test(navigator.userAgent) ||
      /\/(?:account|cart|checkout|checkouts|challenge|password|localization)(?:\/|$)/.test(path) ||
      /\/(?:contact|pages\/contact)(?:\/|$)/.test(path) ||
      Array.from(params.keys()).some((key) => /^(?:preview_|_fd$|pb$|section_id$|sections$|contact_posted$|form_type$|locale$|language$|inquiry_key$|specs$|specs_expected$|config_ref$)/.test(key)) ||
      /contactform|contact_form|challenge/i.test(location.hash);
  }

  function switchLanguage(locale) {
    if (engaged || leaving || document.visibilityState === 'hidden' || !supported.has(locale) ||
        !allowed.has(locale) || locale === root.dataset.currentLocale) return;
    const attempt = locale + '|' + returnPath();
    if (read('sessionStorage', redirectKey) === attempt) return;
    // Persist before navigation. If storage is blocked, leave the page usable instead of risking a loop.
    if (!write('sessionStorage', sessionKey, 'selected:' + locale) ||
        !write('sessionStorage', redirectKey, attempt)) return;
    const form = root.querySelector('form');
    if (!form) return;
    keepReturnPath(form);
    form.querySelector('[name="locale_code"]').value = locale;
    root.dataset.languageStatus = 'redirecting';
    HTMLFormElement.prototype.submit.call(form);
  }

  async function chooseInitialLanguage() {
    if (excluded() || engaged || leaving) {
      root.dataset.languageStatus = 'skipped';
      return;
    }
    // A slow script load must also respect a control that the visitor already focused.
    if (document.activeElement && document.activeElement.matches('input, textarea, select, [contenteditable="true"]')) return;
    const saved = manualPreference();
    if (saved) {
      root.dataset.languageStatus = 'manual-preference';
      if (saved === root.dataset.currentLocale) write('sessionStorage', redirectKey, '');
      switchLanguage(saved);
      return;
    }
    if (read('sessionStorage', sessionKey)) {
      root.dataset.languageStatus = 'session-checked';
      return;
    }
    if (!write('sessionStorage', sessionKey, 'checked')) {
      root.dataset.languageStatus = 'storage-unavailable';
      return;
    }
    root.dataset.languageStatus = 'detecting';
    controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 4000);
    try {
      const base = root.dataset.rootUrl.replace(/\/$/, '') + '/';
      const response = await fetch(base + 'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false', {
        credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (!response.ok) {
        root.dataset.languageStatus = 'lookup-unavailable';
        return;
      }
      const data = await response.json();
      const country = data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
      if (typeof country !== 'string' || !/^[A-Z]{2}$/.test(country) || country === 'XX' || country === 'ZZ') return;
      root.dataset.detectedCountry = country;
      root.dataset.languageStatus = 'detected';
      if (engaged || leaving || controller.signal.aborted) return;
      // IP country is separate from the selected market and from browser language suggestions.
      switchLanguage(manualPreference() || (country === 'CN' ? 'zh-CN' : country === 'HK' ? 'zh-TW' : 'en'));
    } catch (_) {
      if (!engaged && !leaving) root.dataset.languageStatus = 'lookup-unavailable';
      // Timeout, unavailable GeoIP, and restricted storage must not block the storefront.
    } finally {
      window.clearTimeout(timer);
    }
  }

  if (document.visibilityState === 'hidden') {
    const onVisible = () => {
      if (document.visibilityState === 'hidden') return;
      document.removeEventListener('visibilitychange', onVisible);
      chooseInitialLanguage();
    };
    document.addEventListener('visibilitychange', onVisible);
  } else {
    chooseInitialLanguage();
  }
})();
